import { Button, Col, Row, Space, Spin, Tooltip } from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';
import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';
import SearchWidget from './components/SearchWidget';
import AddDiscountModal from './components/AddDiscountModal';
import DiscountItemsList from './components/DiscountItemsList';
import DiscountFilters from './components/DiscountFilters';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { DiscountListItem } from './modules/DiscountItemsList/state';
import { getDiscountsToDisplay, getDiscountFilters, getStatusFilter } from './modules/DiscountItemsList';
import { loadPage } from './loadPage';
import { DiscountListFilters } from 'app/shared/api/getDiscountList';
import { changeStatusFilter, resetFilters } from './filterActions';

interface StateProps {
    list: DiscountListItem[],
    filters: DiscountListFilters,
    statusFilter: ProductStatusFilterValue,
}

interface DispatchProps {
    onStatusFilterChange: (value: ProductStatusFilterValue) => void;
}

export const PageComponent: React.FC<StateProps & DispatchProps> = ({ list, filters, statusFilter, onStatusFilterChange }) => {
    const dispatch = useDispatch();
    const [divisionLoading, setDivisionLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const handleClearFilters = () => {
        void dispatch(resetFilters() as any);
        setShowFilters(false);
    };

    const loadPageAfterDivisionChange = useCallback(async () => {
        setDivisionLoading(true);
        try {
            await loadPage(dispatch, undefined, filters);
        } finally {
            setDivisionLoading(false);
        }
    }, [dispatch, filters]);

    const refreshGridEvent = async () => {
        await loadPage(dispatch, undefined, filters);
    };

    const { status, lastEventReceived } = useSignalREvent([PublishedEventType.DiscountEventPublished], refreshGridEvent );

    useEffect(() => {
        const onHeaderDivisionChange = () => {
            void loadPageAfterDivisionChange();
        };
        window.addEventListener('selectedDivisionChanged', onHeaderDivisionChange);
        return () => {
            window.removeEventListener('selectedDivisionChanged', onHeaderDivisionChange);
        };
    }, [loadPageAfterDivisionChange]);

    useEffect(() => {
        if (!window.location.hash)
            return;

        let id = window.location.hash;
        if (id.startsWith('#'))
            id = id.slice(1);

        const element = document.getElementById(id);
        if (!element)
            return;

        const header = document.getElementById('discounts-header');
        const headerClientRects = header ? header.getClientRects() : null;
        const headerClientRectItem = headerClientRects ? headerClientRects.item(0) : null;
        const offset = headerClientRectItem
            ? headerClientRectItem.top
            : 60;

        const clientRect = element.getClientRects();
        if (clientRect === null || clientRect.length == 0)
            return;

        const clientRectItem = clientRect.item(0);
        if (clientRectItem == null)
            return;

        let scrollPos = clientRectItem.top - offset;
        if (scrollPos < 0)
            scrollPos = 0;
        window.scrollTo(0, scrollPos);
    }, []);

    return (
        <div>
            <h1 id="discounts-header">Discounts</h1>

            <Spin spinning={divisionLoading} description="Loading discounts…" size="large">
                <div style={{ minHeight: 200 }}>
                    <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                        <Col flex="auto" style={{ minWidth: 0, maxWidth: '100%' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <SearchWidget />
                                </div>
                                <ProductStatusFilter
                                    value={statusFilter}
                                    onChange={onStatusFilterChange}
                                />
                            </div>
                        </Col>
                        <Col flex="none" style={{ textAlign: 'right', marginLeft: 'auto' }}>
                            <Space>
                                <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(list) && list.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent} />
                                <Space>
                                    {showFilters && (
                                        <Tooltip title="Clear filters">
                                            <Button icon={<CloseOutlined />} onClick={handleClearFilters} danger />
                                        </Tooltip>
                                    )}
                                    <Tooltip title={`${showFilters ? 'Hide' : 'Show'} filters`}>
                                        <Button icon={<FilterOutlined />} type="primary" onClick={() => setShowFilters(v => !v)} />
                                    </Tooltip>
                                </Space>
                                <AddDiscountModal />
                            </Space>
                        </Col>
                    </Row>

                    {showFilters && (
                    <Row style={{ marginBottom: 16 }}>
                        <Col span={24}>
                            <DiscountFilters />
                        </Col>
                    </Row>
                    )}

                    <DiscountItemsList />
                </div>
            </Spin>
        </div>
    );
}

const mapStateToProps = (state: any): StateProps => ({
    list: getDiscountsToDisplay(state),
    filters: getDiscountFilters(state),
    statusFilter: getStatusFilter(state),
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    onStatusFilterChange: (value: ProductStatusFilterValue) => dispatch(changeStatusFilter(value)),
});

export const Page = connect(mapStateToProps, mapDispatchToProps)(PageComponent);